import { useMemo } from 'react'
import { useIntl } from 'react-intl'

function useTourStyledLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const styledProductTypeTitles: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: 'C23n9vc69HQVczPw0q7C',
        defaultMessage: 'Deluxe',
        description: 'Tour product type title for deluxe tours on the tour page',
      }),
      premium_tour: intl.formatMessage({
        id: 'VWQ3t7-7GdR20QlFbd_E',
        defaultMessage: 'Premium',
        description: 'Tour product type title for premium tours on the tour page',
      }),
      ultra_lux_tour: intl.formatMessage({
        id: 'KoTkJtnY_m9FxPogvYGU',
        defaultMessage: 'Ultra Lux',
        description: 'Tour product type title for ultra lux tours on the tour offer detail page',
      }),
      signature_series_tour: intl.formatMessage({
        id: 'SGzBydaebDZ8CnxQ6gFM',
        defaultMessage: 'Signature Series',
        description: 'Tour product type title for signature series tours',
      }),
      custom_tour: intl.formatMessage({
        id: 'CR2Mqw1P4INsuaA5rRfZ',
        defaultMessage: 'Custom',
        description: 'Tour product type title for custom tours on the tour page',
      }),
      insider_series_tour: intl.formatMessage({
        id: 'ibfwnTAGxkDp3ybhfvtD',
        defaultMessage: 'Insider Series',
        description: 'Tour product type title for insider series tours on offer page',
      }),
    }

    const styledHeading: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: '-T31KnVVMnjByMaXWlQk',
        defaultMessage: 'Key Benefits of Deluxe Tours',
        description: 'Section heading for key benefits of deluxe tours on offer page',
      }),
      premium_tour: intl.formatMessage({
        id: 'jCKmqQP-mTSTpLESXP_t',
        defaultMessage: 'Key Benefits of Premium Tours',
        description: 'Section heading for key benefits of premium tours on offer page',
      }),
      ultra_lux_tour: intl.formatMessage({
        id: 'IwaCT6YTe-eyCJYiwjSi',
        defaultMessage: 'Key Benefits of Ultra Lux Tours',
        description: 'Section heading for key benefits of ultra lux tours',
      }),
      signature_series_tour: intl.formatMessage({
        id: '6OK0zZSIZvXC60jCxi-d',
        defaultMessage: 'Key Benefits of Signature Series Tours',
        description: 'Section heading for key benefits of signature series tours',
      }),
      custom_tour: intl.formatMessage({
        id: 'pnBKTW4A-iMcbEj5Q3Ds',
        defaultMessage: 'Key Benefits of your once-in-a-lifetime tour',
        description: 'Section heading for key benefits of custom once-in-a-lifetime tours',
      }),
      insider_series_tour: intl.formatMessage({
        id: 'hI8ZV1xqRdA8sIhXNREi',
        defaultMessage: 'Key Benefits of Insider Series Tours',
        description: 'Section heading for key benefits of insider series tours',
      }),
    }

    const privateStyledHeading: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: '3njKVnuXqvV9z34CIUc9',
        defaultMessage: 'Key Benefits of Private Deluxe Tours',
        description: 'Section heading for key benefits of private deluxe tours',
      }),
      premium_tour: intl.formatMessage({
        id: 'QshE9jcrpFFmEGe6bWUr',
        defaultMessage: 'Key Benefits of Private Premium Tours',
        description: 'Section heading for key benefits of private premium tours',
      }),
      ultra_lux_tour: intl.formatMessage({
        id: 'v66GBwzlfAkgorKFVn3K',
        defaultMessage: 'Key Benefits of Private Ultra Lux Tours',
        description: 'Section heading for key benefits of private ultra lux tours',
      }),
      signature_series_tour: intl.formatMessage({
        id: '04HbYWE_MyJNe6xJRRcy',
        defaultMessage: 'Key Benefits of Private Signature Series Tours',
        description: 'Section heading for key benefits of private signature series tours',
      }),
      custom_tour: intl.formatMessage({
        id: 'L8FPYD8w2w2TxU1k6Mcf',
        defaultMessage: 'Key benefits of your private once-in-a-lifetime tour',
        description: 'Section heading for key benefits of private custom tours',
      }),
      insider_series_tour: intl.formatMessage({
        id: 'G7tVoRYxEY7rCcY23zkd',
        defaultMessage: 'Key benefits of your private Insider Series tour',
        description: 'Section heading for key benefits of private insider series tours',
      }),
    }

    const styledProductTypeHotelBenefits: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: 'xU91-7Y4YPBh9taho0iM',
        defaultMessage: '4 & 4.5 star hotels',
        description: 'Tour hotel benefit description for deluxe tours on the tour page',
      }),
      premium_tour: intl.formatMessage({
        id: 'dZ2Aoa5FJNYBfQ5hQ6Pf',
        defaultMessage: '5 star hotels',
        description: 'Tour hotel benefit description for premium tours on the tour page',
      }),
      ultra_lux_tour: intl.formatMessage({
        id: 'IkEBSXPmk7HmpFexzQDu',
        defaultMessage: '5 star hotels & iconic stays',
        description: 'Tour hotel benefit description for ultra lux tours',
      }),
      signature_series_tour: intl.formatMessage({
        id: 'BFPuGX81IGOlroqi3_-l',
        defaultMessage: '5 star hotels',
        description: 'Tour hotel benefit description for signature series tours',
      }),
      custom_tour: intl.formatMessage({
        id: 'cyHDoazpcMtTwNTnY9nz',
        defaultMessage: '4 & 5 star hotels',
        description: 'Tour hotel benefit description for custom tours on the tour page',
      }),
      insider_series_tour: '',
    }

    const packageUpgradeDisplayText = {
      REGULAR: intl.formatMessage({
        id: 'zU159GKzORAYE0CDxuiQ',
        defaultMessage: 'Standard Package',
        description: 'Tour package upgrade display text for the standard/regular package tier',
      }),
      VIP: intl.formatMessage({
        id: 'S8-FScl9Ln2H5LZCAGW9',
        defaultMessage: 'Upgraded Package',
        description: 'Tour package upgrade display text for the VIP/upgraded package tier',
      }),
      PLATINUM: intl.formatMessage({
        id: 'Y_qocUusQWI3GQ2xmEIV',
        defaultMessage: 'Deluxe Package',
        description: 'Tour package upgrade display text for the platinum/deluxe package tier',
      }),
    }

    const tourArtefactTypes: Record<App.Tours.ArtefactType, string> = {
      'invitation-letter': intl.formatMessage({
        id: '2H9OjBgrVd_ugNM0-Mi7',
        defaultMessage: 'Letter of Invitation Form',
        description: 'Tour artefact type label for letter of invitation form',
      }),
      'visa': intl.formatMessage({
        id: 'gP9hyw14Cys8Vp8a8mO9',
        defaultMessage: 'Copy of Visa into Destination Country',
        description: 'Tour artefact type label for visa copy into destination country',
      }),
      'passport-photo': intl.formatMessage({
        id: 'ir6WDn-pjVoAKO1Z-pKZ',
        defaultMessage: 'Passport',
        description: 'Tour artefact type label for passport document in the tours section',
      }),
      'passport-photograph': intl.formatMessage({
        id: 'VL9vSVAiukQ089GB37Rm',
        defaultMessage: 'Passport-Sized Photo',
        description: 'Tour artefact type label for passport-sized photograph',
      }),
    }

    return {
      styledProductTypeTitles,
      styledHeading,
      privateStyledHeading,
      styledProductTypeHotelBenefits,
      packageUpgradeDisplayText,
      tourArtefactTypes,
    }
  }, [intl])
}

export default useTourStyledLabels
